// Pages/User/UserReserves.jsx
import React, { useEffect, useState } from "react";
import {
  getUserReserve,
  deleteReservation‌ByUser,
} from "../../Services/ReservationService";
import Loader from "../../Components/Core/Loader";
import SupportWidget from "../../Components/Core/SupportWidget";
import ConfirmModal from "../../Components/Modals/ConfirmModal";
import Notification from "../../Components/Core/Notification";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCalendarXmark,
  faCalendarCheck,
  faClock,
  faBarcode,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import NoResImg from "../../assets/noReserve.png";

const UserReserves = () => {
  // State Management
  const [reserves, setReserves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    reserveId: null,
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Fetch Reserves
  const fetchReserves = async () => {
    try {
      setLoading(true);
      const data = await getUserReserve();
      setReserves(data);
    } catch (error) {
      console.error("Error fetching reserves:", error);
      setSnackbarMessage("خطا در دریافت رزروها");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  // Delete Reserve
  const handleDeleteReserve = async () => {
    try {
      setLoading(true);
      await deleteReservation‌ByUser(deleteModal.reserveId);
      await fetchReserves();
      setSnackbarMessage("رزرو با موفقیت حذف شد");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error deleting reserve:", error);
      setSnackbarMessage("خطا در حذف رزرو. لطفاً دوباره تلاش کنید");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  // Helper Functions
  const openDeleteModal = (reserveId) => {
    setDeleteModal({ open: true, reserveId });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, reserveId: null });
  };

  // Lifecycle
  useEffect(() => {
    document.title = "رزروهای من";
    fetchReserves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render Loading
  if (loading) {
    return <Loader />;
  }

  // Render Empty State
  if (reserves.length === 0) {
    return (
      <>
        <div className="empty-state">
          <div className="empty-state-content">
            <img
              src={NoResImg}
              alt="بدون رزرو"
              className="empty-state-image d-md-none "
            />
            <h3 className="empty-state-title iran-sans">
              شما هیچ رزروی ندارید
            </h3>
            <span className="empty-state-description">
              برای رزرو نوبت جدید به صفحه رزرو نوبت مراجعه کنید
            </span>
          </div>
        </div>
        <SupportWidget />
      </>
    );
  }

  // Render Reserves
  return (
    <>
      <style jsx>{`
        * {
          font-family: "MyPersianFont", "iran-sans", sans-serif;
        }

        .user-reserves-page {
          min-height: calc(100vh - 120px);
          padding: 24px 16px 80px;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%);
        }

        .container {
          max-width: 800px;
          margin: 0 auto;
        }

        /* ============================================ */
        /* Page Header */
        /* ============================================ */

        .page-header {
          margin-bottom: 24px;
          text-align: center;
        }

        .page-title {
          font-size: 26px;
          font-weight: 700;
          color: #053251;
          margin-bottom: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .page-subtitle {
          font-size: 14px;
          color: #6c757d;
          margin: 0;
        }

        /* ============================================ */
        /* Reserve Cards - Responsive Grid */
        /* ============================================ */

        .reserves-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .reserve-card {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          position: relative;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .reserve-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          border-color: rgba(124, 200, 144, 0.3);
          transform: translateY(-2px);
        }

        /* Status Indicator */
        .status-indicator {
          position: absolute;
          top: 0;
          right: 0;
          width: 6px;
          height: 100%;
          border-radius: 0 16px 16px 0;
        }

        .status-indicator.done {
          background: linear-gradient(180deg, #28a745 0%, #20c997 100%);
        }

        .status-indicator.pending {
          background: linear-gradient(180deg, #ffc107 0%, #ff9800 100%);
        }

        /* Card Header */
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #f0f0f0;
        }

        .tracking-code-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tracking-icon {
          color: #7cc890;
          font-size: 18px;
        }

        .tracking-code {
          font-weight: 700;
          color: #053251;
          font-size: 16px;
          letter-spacing: 0.5px;
        }

        .status-badge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }

        .status-done {
          background: #d4edda;
          color: #155724;
        }

        .status-pending {
          background: #fff3cd;
          color: #856404;
        }

        /* Card Body */
        .card-body {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .info-label {
          font-size: 12px;
          color: #6c757d;
          font-weight: 500;
        }

        .info-value {
          font-size: 15px;
          color: #053251;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .info-icon {
          color: #7cc890;
          font-size: 16px;
        }

        /* Card Footer */
        .card-footer {
          display: flex;
          justify-content: flex-end;
          padding-top: 12px;
          border-top: 1px solid #f0f0f0;
        }

        .btn-delete-card {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          border: none;
          color: white;
          padding: 10px 20px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .btn-delete-card:hover {
          background: linear-gradient(135deg, #c82333 0%, #bd2130 100%);
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
          transform: translateY(-2px);
        }

        .btn-delete-card:active {
          transform: translateY(0);
        }

        .btn-disabled {
          background: #e9ecef;
          color: #6c757d;
          cursor: not-allowed;
        }

        .btn-disabled:hover {
          background: #e9ecef;
          box-shadow: none;
          transform: none;
        }

        /* ============================================ */
        /* Empty State */
        /* ============================================ */

        .empty-state {
          min-height: calc(100vh - 200px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf0 100%);
        }

        .empty-state-content {
          text-align: center;
          max-width: 400px;
        }

        .empty-state-image {
          width: 220px;
          height: 220px;
          opacity: 0.9;
          margin-bottom: 24px;
          animation: float 3s ease-in-out infinite;
        }

        .empty-state-title {
          font-size: 22px;
          font-weight: 700;
          color: #053251;
          margin-bottom: 12px;
        }

        .empty-state-description {
          font-size: 15px;
          color: #6c757d;
          margin: 0;
        }

        /* ============================================ */
        /* Info Note */
        /* ============================================ */

        .info-note {
          background: linear-gradient(
            135deg,
            rgba(124, 200, 144, 0.1) 0%,
            rgba(124, 200, 144, 0.05) 100%
          );
          border: 1px solid rgba(124, 200, 144, 0.3);
          border-radius: 12px;
          padding: 14px 18px;
          margin-top: 20px;
        }

        .info-note p {
          margin: 0;
          font-size: 13px;
          color: #053251;
          line-height: 1.6;
        }

        /* ============================================ */
        /* Animations */
        /* ============================================ */

        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        /* ============================================ */
        /* Responsive Breakpoints */
        /* ============================================ */

        /* Desktop Large (1400px+) */
        @media (min-width: 1400px) {
          .container {
            max-width: 1200px;
          }

          .reserves-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* Desktop (992px - 1399px) */
        @media (min-width: 992px) and (max-width: 1399px) {
          .container {
            max-width: 960px;
          }

          .reserves-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Tablet (768px - 991px) */
        @media (min-width: 768px) and (max-width: 991px) {
          .container {
            max-width: 720px;
          }

          .reserves-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }

          .reserve-card {
            padding: 18px;
          }
        }

        /* Mobile Large (577px - 767px) */
        @media (min-width: 577px) and (max-width: 767px) {
          .reserves-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        /* Mobile (max-width: 576px) */
        @media (max-width: 576px) {
          .reserves-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .user-reserves-page {
            padding: 16px 12px 80px;
          }

          .page-title {
            font-size: 22px;
          }

          .page-subtitle {
            font-size: 13px;
          }

          .reserve-card {
            padding: 16px;
          }

          .card-body {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .tracking-code {
            font-size: 14px;
          }

          .status-badge {
            font-size: 12px;
            padding: 5px 12px;
          }

          .info-value {
            font-size: 14px;
          }

          .btn-delete-card {
            width: 100%;
            justify-content: center;
            padding: 12px;
          }

          .empty-state-image {
            width: 180px;
            height: 180px;
          }

          .empty-state-title {
            font-size: 20px;
          }

          .empty-state-description {
            font-size: 14px;
          }
        }

        /* Mobile Extra Small (max-width: 375px) */
        @media (max-width: 375px) {
          .card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .tracking-code-wrapper {
            width: 100%;
          }

          .status-badge {
            align-self: flex-start;
          }

          .reserves-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <div className="user-reserves-page">
        <div className="container">
          {/* Header */}
          <div className="page-header">
            <h1 className="page-title">
              <FontAwesomeIcon icon={faCalendarXmark} />
              رزروهای من
            </h1>
            <p className="page-subtitle">مشاهده و مدیریت نوبت‌های رزرو شده</p>
          </div>

          {/* Reserves Grid */}
          <div className="reserves-grid">
            {reserves.map((reserve) => (
              <div key={reserve.code} className="reserve-card">
                {/* Status Indicator */}
                <div
                  className={`status-indicator ${
                    reserve.isDone ? "done" : "pending"
                  }`}
                />

                {/* Card Header */}
                <div className="card-header">
                  <div className="tracking-code-wrapper">
                    <FontAwesomeIcon
                      icon={faBarcode}
                      className="tracking-icon"
                    />
                    <span className="tracking-code">{reserve.code}</span>
                  </div>
                  <span
                    className={`status-badge ${
                      reserve.isDone ? "status-done" : "status-pending"
                    }`}
                  >
                    {reserve.isDone ? "✓ تمام شده" : "⏳ در انتظار"}
                  </span>
                </div>

                {/* Card Body */}
                <div className="card-body">
                  <div className="info-item">
                    <span className="info-label">تاریخ نوبت</span>
                    <span className="info-value">
                      <FontAwesomeIcon
                        icon={faCalendarCheck}
                        className="info-icon"
                      />
                      {reserve.dateDisplay}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">ساعت نوبت</span>
                    <span className="info-value">
                      <FontAwesomeIcon icon={faClock} className="info-icon" />
                      {reserve.startTime}
                    </span>
                  </div>
                </div>

                {/* Card Footer */}
                <div className="card-footer">
                  {reserve.canDelete ? (
                    <button
                      className="btn-delete-card"
                      onClick={() => openDeleteModal(reserve.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      لغو نوبت
                    </button>
                  ) : (
                    <button className="btn-delete-card btn-disabled" disabled>
                      <FontAwesomeIcon icon={faXmark} />
                      غیر قابل لغو
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Info Note */}
          <div className="info-note">
            <p>
              💡 <strong>نکته:</strong> برای جابجایی نوبت با مطب یا پشتیبانی در
              تماس باشید
            </p>
          </div>
        </div>
      </div>

      {/* Support Widget */}
      <SupportWidget />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        openDialog={deleteModal.open}
        handleCloseDialog={closeDeleteModal}
        handleConfirm={handleDeleteReserve}
        text={[
          "آیا از لغو این نوبت مطمئن هستید؟",
          "این عملیات قابل بازگشت نیست.",
        ]}
        cancleText="خیر"
        confirmText="بله، لغو شود"
        title="لغو نوبت"
      />

      {/* Notification */}
      <Notification
        openSnackbar={openSnackbar}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        setOpenSnackbar={setOpenSnackbar}
      />
    </>
  );
};

export default UserReserves;
